import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface SubscriptionData {
  id: string;
  user_id: string;
  status: 'trial' | 'active' | 'expired' | 'cancelled';
  trial_start_date: string;
  trial_end_date: string;
  subscription_start_date: string | null;
  subscription_end_date: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
}

export const useSubscription = () => {
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState<number | null>(null);
  const [isTrialExpiringSoon, setIsTrialExpiringSoon] = useState(false);
  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setLoading(false);
        return;
      }

      // Get session for API calls
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        setLoading(false);
        return;
      }

      // Check Stripe subscription status
      const { data: stripeData, error: stripeError } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      // Successfully synced with Stripe if no error and subscribed

      // Fetch local subscription data
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      setSubscription(data);

      // Calculate access and days remaining
      if (data) {
        const now = new Date();
        const trialEnd = new Date(data.trial_end_date);
        const isInTrial = data.status === 'trial' && trialEnd > now;
        const isActive = data.status === 'active';
        
        // Auto-update expired trials
        if (data.status === 'trial' && trialEnd <= now) {
          const { error: updateError } = await supabase
            .from('user_subscriptions')
            .update({ status: 'expired', updated_at: new Date().toISOString() })
            .eq('user_id', user.id);
          
          if (updateError) {
            console.error('Error updating expired trial:', updateError);
          } else {
            setSubscription({ ...data, status: 'expired' });
          }
        }
        
        setHasAccess(isInTrial || isActive);

        if (isInTrial) {
          const diffTime = trialEnd.getTime() - now.getTime();
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          setDaysRemaining(diffDays);
          setIsTrialExpiringSoon(diffDays <= 3);
        } else {
          setDaysRemaining(null);
          setIsTrialExpiringSoon(false);
        }
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  return {
    subscription,
    loading,
    hasAccess,
    daysRemaining,
    isTrialExpiringSoon,
    refetch: fetchSubscription
  };
};
